/* ─────────────────────────────────────────────────────────────────────────
   Collapsed dashboard hub
   • Banner + summary strip (reused / copied from DashboardV2)
   • "Needs attention" — clean alert rows: device name + reason on one line.
     Tapping a row opens that device's detail page.
   • Category tiles → Water Level / Pump-Borewell / Sense / Valves, each with
     online·offline plus category status (running, wet/dry, open/closed).
   • Gateways — total / online / offline shown in the summary strip.
   Current DashboardV2 is left completely untouched.
   ──────────────────────────────────────────────────────────────────────── */
import axios from "axios";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { FiAlertTriangle } from "react-icons/fi";
import { FaChevronRight } from "react-icons/fa";
import { BiWater, BiCog, BiPulse } from "react-icons/bi";
import { GiValve } from "react-icons/gi";
import { GlobalState } from "../../GlobalState";
import { DashboardSvg } from "../../assets/svg";
import Banner from "../utils/banner";
import SplashScreen from "../utils/SplashScreen";
import GatewayBoxComponent from "../sensor/gatwayDashboard";
import { useSensorList, useHubDerived } from "./dashV3.data";
import {
  PageContainer, ContentContainer, AlertBadge,
  SummaryStrip, SummaryItem, SummaryCount, SummaryLabel, TipItem, Tip,
  TileGrid, Tile, TileHead, TileCount, TileSub, TileMsg, Msg,
  AlertCard, AlertHead, AlertItem, AlertDot, AlertText, AlertName, AlertReason, AlertSince,
} from "./dashV3.styles";

import { useDashboardReady } from "../utils/dashboardReady";


const dotColor = (a) => {
  if (a.kind === "low") return "#EA580C";
  if (a.kind === "overflow" || a.kind === "high" || a.kind === "faulty") return "#F59E0B";
  return "#9CA3AF"; // offline
};

const reasonText = (a) => {
  switch (a.kind) {
    case "faulty":   return "Sensor faulty — check device";
    case "overflow": return `Overflow · ${Math.round(a.level)}%`;
    case "high":     return `Water level high · ${Math.round(a.level)}% (max ${a.max}%)`;
    case "low":      return `Water level low · ${Math.round(a.level)}% (min ${a.min}%)`;
    case "offline":
      if (a.category === "water")    return "Water sensor offline";
      if (a.category === "sense")    return "Water-sense offline";
      if (a.category === "borewell") return "Borewell offline";
      if (a.category === "valve")    return "Valve offline";
      if (a.category === "gateway")  return "Gateway offline";
      return "Pump offline";
    default: return "";
  }
};

const routeFor = (a) => {
  if (a.category === "gateway") return null; // no gateway detail page yet — set a route here if you have one
  if (a.category === "water") return `/sensor/${a.sensor_id}/${a.type}`;
  if (a.category === "sense") return `/sensor/${a.sensor_id}/Water Sense`;
  if (a.category === "valve") return `/solenoid/${a.solenoid_id}/${a.name || ""}`;
  return `/motor/${a.motor_id}/sensor/${a.sensor_id}/Pump - ${a.name || ""}`;
};

const alertKey = (a) =>
  a.category === "gateway"
    ? `gateway-${a.name}`
    : `${a.category}-${a.sensor_id || a.motor_id || a.solenoid_id}`;

/* Render a " · "-joined status line from [text, color] segments */
function statusSegments(segs) {
  return segs.filter(Boolean).map((s, i) => (
    <React.Fragment key={i}>
      {i > 0 && " · "}
      <span style={{ color: s[1] || "#4b5563" }}>{s[0]}</span>
    </React.Fragment>
  ));
}

const GREEN = "#16A34A";
const GREY = "#9CA3AF";

export default function DashboardHubV3() {
  const state = useContext(GlobalState);
  const history = useHistory();
  const { setReady } = useDashboardReady();
  const { loading, water, pumps, borewells, valves } = useSensorList();
  const ready = !loading;
  const { loading: deriving, counts, alerts, waterStats, pbStats, senseStats, valveStats } =
    useHubDerived({ water, pumps, borewells, valves, ready });

  /* ── Gateways ──────────────────────────────────────────────────────────
     gatewaySensors : gateway entries from the sensor list
     gatewayList    : live status reported by each hidden GatewayBoxComponent */
  const [gatewaySensors, setGatewaySensors] = useState([]);
  const [gatewayList, setGatewayList] = useState([]); // [{ name, isOnline }]

  const reportGateway = useCallback((name, isOnline) => {
    setGatewayList((prev) => {
      const exists = prev.find((g) => g.name === name);
      if (exists && exists.isOnline === isOnline) return prev;
      if (exists) return prev.map((g) => (g.name === name ? { ...g, isOnline } : g));
      return [...prev, { name, isOnline }];
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_API}/user/v2/sensor_list`, {
        headers: { Authorization: state.token[0] },
      })
      .then((res) => {
        if (cancelled) return;
        setGatewaySensors(
          (res.data || []).flatMap((item) =>
            (item.sensors || []).filter((s) => s?.type === "Gateway")
          )
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [state.token]);

  const gwTotal       = gatewaySensors.length;
  const gwReported    = gatewayList.length >= gwTotal; // every gateway has reported
  const gwOnlineList  = gatewayList.filter((g) => g.isOnline);
  const gwOnline      = gwOnlineList.length;
  const gwOfflineList = gatewayList.filter((g) => !g.isOnline);
  const gwOffline     = gwOfflineList.length;

  /* Offline tooltip color: all gateways offline → red, otherwise amber */
  const gwTipBg =
    gwTotal > 0 && gwOffline === gwTotal ? "#DC2626" : "#D97706";

  useEffect(() => {
    if (!loading) setReady(true);
  }, [loading, setReady]);

  useEffect(() => {
    return () => setReady(false);
  }, [setReady]);

  if (loading) return null; // overlay in Pages.js still covers this

  const totalNow = water.length + pumps.length + borewells.length + valves.length;

  /* Offline gateways become alert rows, appended after the device alerts */
  const gatewayAlerts = gwOfflineList.map((g) => ({
    kind: "offline",
    category: "gateway",
    name: g.name,
  }));
  const allAlerts = [...alerts, ...gatewayAlerts];
  const alertsReady = !deriving && gwReported; // wait for gateways before saying "all healthy"

  const waterMsgSegs = [
    waterStats.overflow ? [`${waterStats.overflow} overflow`, "#B24418"] : null,
    waterStats.high ? [`${waterStats.high} water level high`, "#B45309"] : null,
    waterStats.low ? [`${waterStats.low} water level low`, "#EA580C"] : null,
  ].filter(Boolean);

  return (
    <PageContainer>
      {/* Hidden gateway components — they only report online/offline status */}
      {gatewaySensors.map((sensor, i) => (
        <GatewayBoxComponent
          key={`gw-${i}`}
          sensor={sensor}
          onStatusReport={reportGateway}
          hidden
        />
      ))}

      <Link to="/profile">
        <Banner title={<span style={{ color: "#107066" }}>Welcome {state.userAPI.name}</span>} subtitle="" tooltip="View Your Profile">
          <DashboardSvg />
        </Banner>
      </Link>

      <SummaryStrip>
        <SummaryItem>
          <SummaryCount $color="#111827">{counts.total || totalNow}</SummaryCount>
          <SummaryLabel>Devices</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryCount $color="#16A34A">{deriving ? "—" : counts.online}</SummaryCount>
          <SummaryLabel>Online</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryCount $color="#9CA3AF">{deriving ? "—" : counts.offline}</SummaryCount>
          <SummaryLabel>Offline</SummaryLabel>
        </SummaryItem>

        {/* Gateways — total, online, offline (same look as devices) */}
        {gwTotal > 0 && (
          <>
            <SummaryItem>
              <SummaryCount $color="#111827">{gwTotal}</SummaryCount>
              <SummaryLabel>Gateways</SummaryLabel>
            </SummaryItem>
            <TipItem $hasTip={gwOnline > 0}>
              <SummaryCount $color="#16A34A">{gwReported ? gwOnline : "—"}</SummaryCount>
              <SummaryLabel>GW Online</SummaryLabel>
              {gwOnline > 0 && (
                <Tip $bg="#16A34A">
                  {gwOnlineList.map((g) => (
                    <div key={g.name}>∗ {g.name}</div>
                  ))}
                </Tip>
              )}
            </TipItem>
            <TipItem $hasTip={gwOffline > 0}>
              <SummaryCount $color="#9CA3AF">{gwReported ? gwOffline : "—"}</SummaryCount>
              <SummaryLabel>GW Offline</SummaryLabel>
              {gwOffline > 0 && (
                <Tip $bg={gwTipBg}>
                  {gwOfflineList.map((g) => (
                    <div key={g.name}>∗ {g.name}</div>
                  ))}
                </Tip>
              )}
            </TipItem>
          </>
        )}
      </SummaryStrip>

      <ContentContainer>
        {/* Needs attention — one row per alert, reason inline with the name */}
        {!deriving && allAlerts.length > 0 && (
          <AlertCard>
            <AlertHead>
              <FiAlertTriangle />
              Needs attention
              <AlertBadge>{allAlerts.length}</AlertBadge>
            </AlertHead>
            {allAlerts.map((a) => {
              const route = routeFor(a);
              return (
                <AlertItem
                  key={alertKey(a)}
                  onClick={route ? () => history.push(route) : undefined}
                  style={route ? undefined : { cursor: "default" }}
                >
                  <AlertDot $color={dotColor(a)} />
                  <AlertText>
                    <AlertName>{a.name}</AlertName>
                    <AlertReason>{reasonText(a)}</AlertReason>
                  </AlertText>
                  {a.since && <AlertSince>{a.since}</AlertSince>}
                  {route && <FaChevronRight color="#9CA3AF" size={12} />}
                </AlertItem>
              );
            })}
          </AlertCard>
        )}
        {alertsReady && allAlerts.length === 0 && (
          <Msg>All devices healthy — nothing needs attention.</Msg>
        )}

        {/* Category tiles — line 1: online·offline, line 2: message / status */}
        <TileGrid>
          {/* Water Level — only when the customer has tanks */}
          {water.length > 0 && (
            <Tile onClick={() => history.push("/water")}>
              <TileHead><span style={{ display: "flex", alignItems: "center", gap: 6 }}><BiWater /> Water Level</span><FaChevronRight color="#9CA3AF" size={12} /></TileHead>
              <TileCount><b>{water.length}</b> tanks</TileCount>
              <TileSub>{deriving ? "checking…" : statusSegments([
                [`${waterStats.online} online`, GREEN],
                [`${waterStats.offline} offline`, waterStats.offline ? GREY : "#4b5563"],
              ])}</TileSub>
              {!deriving && (
                <TileMsg>{waterMsgSegs.length
                  ? statusSegments(waterMsgSegs)
                  : <span style={{ color: "#9CA3AF" }}>levels normal</span>}</TileMsg>
              )}
            </Tile>
          )}

          {/* Pump / Borewell — only when the customer has pumps or borewells */}
          {(pumps.length + borewells.length) > 0 && (
            <Tile onClick={() => history.push("/pumps")}>
              <TileHead><span style={{ display: "flex", alignItems: "center", gap: 6 }}><BiCog /> Pump / Borewell</span><FaChevronRight color="#9CA3AF" size={12} /></TileHead>
              <TileCount><b>{pumps.length + borewells.length}</b> devices</TileCount>
              <TileSub>{deriving ? "checking…" : statusSegments([
                [`${pbStats.online} online`, GREEN],
                [`${pbStats.offline} offline`, pbStats.offline ? GREY : "#4b5563"],
              ])}</TileSub>
              {!deriving && (
                <TileMsg>{statusSegments([
                  [`${pbStats.motorsRunning} motor on`, pbStats.motorsRunning ? GREEN : "#4b5563"],
                  [`${pbStats.borewellsRunning} borewell on`, pbStats.borewellsRunning ? GREEN : "#4b5563"],
                ])}</TileMsg>
              )}
            </Tile>
          )}

          {/* Sense — only when the customer has water-sense devices */}
          {senseStats.total > 0 && (
            <Tile onClick={() => history.push("/sense")}>
              <TileHead><span style={{ display: "flex", alignItems: "center", gap: 6 }}><BiPulse /> Sense</span><FaChevronRight color="#9CA3AF" size={12} /></TileHead>
              <TileCount><b>{senseStats.total}</b> devices</TileCount>
              <TileSub>{statusSegments([
                [`${senseStats.online} online`, GREEN],
                [`${senseStats.offline} offline`, senseStats.offline ? GREY : "#4b5563"],
              ])}</TileSub>
              <TileMsg>{statusSegments([
                [`${senseStats.wet} wet`, "#1AA3EC"],
                [`${senseStats.dry} dry`, "#d9594c"],
              ])}</TileMsg>
            </Tile>
          )}

          {/* Valves — only when the customer has solenoid valves */}
          {valves.length > 0 && (
            <Tile onClick={() => history.push("/valves")}>
              <TileHead><span style={{ display: "flex", alignItems: "center", gap: 6 }}><GiValve /> Valves</span><FaChevronRight color="#9CA3AF" size={12} /></TileHead>
              <TileCount><b>{valves.length}</b> valves</TileCount>
              <TileSub>{deriving ? "checking…" : statusSegments([
                [`${valveStats.online} online`, GREEN],
                [`${valveStats.offline} offline`, valveStats.offline ? GREY : "#4b5563"],
              ])}</TileSub>
              {!deriving && (
                <TileMsg>{statusSegments([
                  [`${valveStats.open} open`, "#237A35"],
                  [`${valveStats.closed} closed`, "#6B7280"],
                ])}</TileMsg>
              )}
            </Tile>
          )}
        </TileGrid>
      </ContentContainer>
    </PageContainer>
  );
}